const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

export const dateFromLocalIso = (value) => {
	if (!DATE_PATTERN.test(value || '')) return null;
	const [year, month, day] = value.split('-').map(Number);
	return new Date(year, month - 1, day);
};

export const dateToLocalIso = (date) => {
	if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');
	return `${year}-${month}-${day}`;
};

const currentDateInTimezone = (timezone, referenceDate) => {
	try {
		const parts = new Intl.DateTimeFormat('en-CA', {
			timeZone: timezone,
			year: 'numeric',
			month: '2-digit',
			day: '2-digit',
		}).formatToParts(referenceDate);
		const values = parts.reduce((result, part) => ({ ...result, [part.type]: part.value }), {});
		return new Date(Number(values.year), Number(values.month) - 1, Number(values.day));
	} catch (error) {
		return new Date(referenceDate.getFullYear(), referenceDate.getMonth(), referenceDate.getDate());
	}
};

export const getOperationalClosureDateBounds = (timezone, referenceDate = new Date()) => {
	const today = currentDateInTimezone(timezone || 'UTC', referenceDate);
	const firstAllowedDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);
	const lastAllowedDate = new Date(today.getFullYear() + 1, 11, 31);
	return { firstAllowedDate, lastAllowedDate };
};

const datesOverlap = (startsOnLocal, endsOnLocal, closure) =>
	startsOnLocal <= closure.endsOnLocal && endsOnLocal >= closure.startsOnLocal;

export const validateOperationalClosure = (draft, bounds, closures = [], editingClosureId = null) => {
	const errors = {};
	const name = draft.name?.trim() || '';
	const startsOnLocal = dateToLocalIso(draft.dates?.[0]);
	const endsOnLocal = dateToLocalIso(draft.dates?.[1] || draft.dates?.[0]);
	const firstAllowedDate = dateToLocalIso(bounds.firstAllowedDate);
	const lastAllowedDate = dateToLocalIso(bounds.lastAllowedDate);

	if (!name) {
		errors.name = 'Give this closure a name, such as Winter closure or Public holiday.';
	} else if (name.length > 60) {
		errors.name = 'The closure name can contain up to 60 characters.';
	}

	if (!startsOnLocal) {
		errors.dates = 'Select a future date or a continuous date range.';
	} else if (startsOnLocal < firstAllowedDate) {
		errors.dates = 'Closures must start after the machine’s current local date.';
	} else if (endsOnLocal > lastAllowedDate) {
		errors.dates = 'Closures can only be planned through the end of next year.';
	} else if (endsOnLocal < startsOnLocal) {
		errors.dates = 'The end date must be on or after the start date.';
	} else {
		const overlappingClosure = closures.find(
			(closure) => closure.closureId !== editingClosureId && datesOverlap(startsOnLocal, endsOnLocal, closure),
		);
		if (overlappingClosure) {
			errors.dates = `This overlaps “${overlappingClosure.name}” (${overlappingClosure.startsOnLocal} to ${overlappingClosure.endsOnLocal}).`;
		}
	}

	return {
		isValid: Object.keys(errors).length === 0,
		errors,
		payload: {
			name,
			startsOnLocal,
			endsOnLocal: endsOnLocal || startsOnLocal,
		},
	};
};

export const formatOperationalClosureDateRange = (startsOnLocal, endsOnLocal) => {
	const formatter = new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
	const start = dateFromLocalIso(startsOnLocal);
	const end = dateFromLocalIso(endsOnLocal);
	if (!start || !end) return 'Invalid date range';
	if (startsOnLocal === endsOnLocal) return formatter.format(start);
	return `${formatter.format(start)} – ${formatter.format(end)}`;
};
